import Rollbar from 'rollbar';
import { logAnalyticsEvent } from '@/utils/Analytics';
import { applyInternalTrafficFromQuery, isInternalTraffic } from '@/utils/internalTraffic';

// A production BUILD served on localhost (e.g. `python -m http.server` on dist/ to eyeball
// prerender output) otherwise reports to the PROD Rollbar project — self-inflicted noise,
// the same leak class as GA4 internal traffic. Treat localhost as never-report unless a dev
// explicitly opts in with VITE_ENABLE_ERROR_MONITORING=true.
function isLocalHost(): boolean {
  if (typeof window === 'undefined' || !window.location) return false;
  const h = window.location.hostname;
  return h === 'localhost' || h === '127.0.0.1' || h === '0.0.0.0' || h === '[::1]' || h.endsWith('.local');
}

/**
 * Only our own domains may report errors. Crawlers/scrapers re-render our
 * prerendered pages on THEIR hosts (observed: an AI scraper serving our blog
 * from 127.0.0.1:8000, whose broken asset paths threw "Unexpected token '<'"
 * ×111 into OUR Rollbar — the localhost opt-in below let it through because
 * VITE_ENABLE_ERROR_MONITORING=true is set in the prod build). Vercel preview
 * deploys stay reportable.
 */
function isOwnHost(): boolean {
  if (typeof window === 'undefined' || !window.location) return false;
  const h = window.location.hostname;
  return /(^|\.)socialworktestprepacademy\.com$/.test(h) || h.endsWith('.vercel.app');
}

// Configuration interface for error monitoring
interface ErrorMonitoringConfig {
  rollbarAccessToken?: string;
  environment: 'development' | 'staging' | 'production';
  captureUncaught: boolean;
  captureUnhandledRejections: boolean;
  enabled: boolean;
}

// Error context interface
interface ErrorContext {
  component?: string;
  action?: string;
  userId?: string;
  sessionId?: string;
  metadata?: Record<string, unknown>;
  level?: 'critical' | 'error' | 'warning' | 'info' | 'debug';
}

class ErrorMonitoringService {
  private rollbar: Rollbar | null = null;
  private config: ErrorMonitoringConfig;
  private isInitialized = false;

  constructor() {
    this.config = {
      rollbarAccessToken: import.meta.env.ROLLBAR_ACCESS_TOKEN,
      environment: import.meta.env.PROD ? 'production' : 'development',
      captureUncaught: true,
      captureUnhandledRejections: true,
      // Report only from hosts we own. The dev escape hatch is localhost-ONLY
      // and requires the explicit flag — a foreign host mirroring our pages
      // can never satisfy either branch.
      enabled: (import.meta.env.PROD || import.meta.env.VITE_ENABLE_ERROR_MONITORING === 'true')
        && (isOwnHost()
          || (isLocalHost() && import.meta.env.VITE_ENABLE_ERROR_MONITORING === 'true' && !import.meta.env.PROD))
    };
  }

  /**
   * Initialize error monitoring service
   */
  public initialize(): void {
    if (this.isInitialized) {
      return;
    }

    // Internal traffic never reports to Rollbar. Error monitoring boots before
    // any page effect runs, so read ?internal=1 straight from the URL here
    // (same account/device flag GA4 uses). Without this gate, founder sessions
    // and automated funnel walks feed the error stream and page the founder.
    applyInternalTrafficFromQuery();
    if (isInternalTraffic()) {
      console.log('ℹ️ Error monitoring disabled (internal traffic)');
      this.isInitialized = true;
      return;
    }

    try {
      if (this.config.enabled && this.config.rollbarAccessToken) {
        this.rollbar = new Rollbar({
          accessToken: this.config.rollbarAccessToken,
          environment: this.config.environment,
          captureUncaught: this.config.captureUncaught,
          captureUnhandledRejections: this.config.captureUnhandledRejections,
          // Email-security scanners (Microsoft Defender SafeLinks et al.) open
          // our email links in instrumented headless browsers whose OWN injected
          // scripts throw — 41 hits within an hour of the 2026-08-03 exam-day
          // send. Fires after every email blast to corporate inboxes; never a
          // real user. Kill it at the source.
          ignoredMessages: [
            'Object Not Found Matching Id',
          ],
          payload: {
            client: {
              javascript: {
                source_map_enabled: true,
                code_version: import.meta.env.VITE_APP_VERSION || 'unknown'
              }
            }
          },
          // Transform payload to add custom context (safe for jsdom/node)
          transform: (payload) => {
            const hasNavigator = typeof navigator !== 'undefined' && typeof navigator.userAgent === 'string';
            const hasWindow = typeof window !== 'undefined' && typeof window.location?.href === 'string';
            const ua = hasNavigator ? navigator.userAgent : undefined;
            const url = hasWindow ? window.location?.href : undefined;
            payload.custom = {
              ...(payload.custom || {}),
              timestamp: Date.now(),
              ...(ua ? { userAgent: ua } : {}),
              ...(url ? { url } : {})
            };
          }
        });

        console.log('✅ Error monitoring initialized successfully');
      } else {
        console.log('ℹ️ Error monitoring disabled or missing configuration');
      }
    } catch (error) {
      console.error('❌ Failed to initialize error monitoring:', error);
    }

    this.isInitialized = true;
  }

  /**
   * Log an error with context
   */
  public logError(error: Error, context?: ErrorContext): void {
    try {
      // Use smart error tracking directly from Analytics
      logAnalyticsEvent('error_occurred', {
        error_type: error.name,
        error_message: error.message,
        stack_trace: error.stack,
        component: context?.component,
        timestamp: Date.now(),
        impact_level: context?.level === 'critical' ? 'critical' : 
                     context?.level === 'error' ? 'high' : 'medium',
        ...context?.metadata
      });

      // Log to Rollbar if available
      if (this.rollbar) {
        const rollbarContext = {
          custom: {
            component: context?.component,
            action: context?.action,
            level: context?.level || 'error',
            userId: context?.userId,
            sessionId: context?.sessionId,
            ...context?.metadata
          }
        };

        this.rollbar.error(error, rollbarContext);
      }

      // Log to console in development
      if (this.config.environment === 'development') {
        console.error('🔴 Error logged:', {
          error: error.message,
          stack: error.stack,
          context
        });
      }
    } catch (loggingError) {
      console.error('Failed to log error:', loggingError);
    }
  }

  /**
   * Log an info message
   */
  public logInfo(message: string, context?: ErrorContext): void {
    try {
      if (this.rollbar) {
        this.rollbar.info(message, {
          custom: {
            component: context?.component,
            action: context?.action,
            ...context?.metadata
          }
        });
      }

      if (this.config.environment === 'development') {
        console.log('ℹ️ Info logged:', message, context);
      }
    } catch (error) {
      console.error('Failed to log info:', error);
    }
  }

  /**
   * Log a warning
   */
  public logWarning(message: string, context?: ErrorContext): void {
    try {
      if (this.rollbar) {
        this.rollbar.warning(message, {
          custom: {
            component: context?.component,
            action: context?.action,
            ...context?.metadata
          }
        });
      }

      if (this.config.environment === 'development') {
        console.warn('⚠️ Warning logged:', message, context);
      }
    } catch (error) {
      console.error('Failed to log warning:', error);
    }
  }

  /**
   * Set user context for all future errors
   */
  public setUserContext(userId: string, userData?: Record<string, unknown>): void {
    try {
      if (this.rollbar) {
        this.rollbar.configure({
          payload: {
            person: {
              id: userId,
              email: userData?.email as string | undefined,
              username: userData?.username as string | undefined,
              ...userData
            }
          }
        });
      }
    } catch (error) {
      console.error('Failed to set user context:', error);
    }
  }

  /**
   * Clear user context
   */
  public clearUserContext(): void {
    try {
      if (this.rollbar) {
        this.rollbar.configure({
          payload: {
            person: undefined
          }
        });
      }
    } catch (error) {
      console.error('Failed to clear user context:', error);
    }
  }

  /**
   * Log critical errors that need immediate attention
   */
  public logCritical(error: Error, context?: ErrorContext): void {
    this.logError(error, { ...context, level: 'critical' });
  }

  /**
   * Check if error monitoring is enabled
   */
  public isEnabled(): boolean {
    return this.config.enabled && this.rollbar !== null;
  }

  /**
   * Get current configuration
   */
  public getConfig(): ErrorMonitoringConfig {
    return { ...this.config };
  }
}

// Export singleton instance
export const errorMonitoring = new ErrorMonitoringService();

// Export types for use in other files
export type { ErrorContext, ErrorMonitoringConfig };
